
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from '../../../hooks/useTranslation';

export default function AboutSection() {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const skills = [
    {
      title: t('skill1Title'),
      description: t('skill1Desc'),
      icon: "ri-shield-check-line"
    },
    {
      title: t('skill2Title'),
      description: t('skill2Desc'),
      icon: "ri-spy-line"
    },
    {
      title: t('skill3Title'),
      description: t('skill3Desc'),
      icon: "ri-search-eye-line"
    },
    {
      title: t('skill4Title'),
      description: t('skill4Desc'),
      icon: "ri-file-text-line"
    },
    {
      title: t('skill5Title'),
      description: t('skill5Desc'),
      icon: "ri-database-2-line"
    },
    {
      title: t('skill6Title'),
      description: t('skill6Desc'),
      icon: "ri-team-line"
    },
    {
      title: t('skill7Title'),
      description: t('skill7Desc'),
      icon: "ri-bug-line"
    }
  ];

  return (
    <section ref={sectionRef} id="about" className="py-20 bg-gray-900 relative overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%2306b6d4' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='1'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />
      </div>

      <div className="max-w-6xl mx-auto px-6 relative z-10">
        <div className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          {/* Section header */}
          <div className="text-center mb-16">
            <div className="font-mono text-cyan-400 text-sm mb-4">
              <span className="text-gray-500">root@cyberintel:~$</span> {t('aboutTerminal')}
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 font-mono">
              <span className="text-cyan-400">&gt;</span> {t('aboutTitle')}
            </h2>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Personal description */}
            <div className="space-y-6">
              <div className="bg-black/50 border border-gray-700 p-8 rounded-lg">
                <div className="font-mono text-cyan-400 text-sm mb-4">
                  <span className="text-gray-500">{t('personalIntroduction')}</span>
                </div>
                <div className="text-gray-300 leading-relaxed space-y-4 font-mono text-sm">
                  <p>
                    <strong className="text-white">{t('aboutIntro')}</strong>
                  </p>
                  <p>
                    {t('aboutP1')}
                  </p>
                  <p>
                    {t('aboutP2')}
                  </p>
                  <p>
                    {t('aboutP3')}
                  </p>
                  <p>
                    {t('aboutP4')}
                  </p>
                  <p>
                    {t('aboutP5')}
                  </p>
                </div>
              </div>

              {/* LinkedIn Box */}
              <div className="bg-black/50 border border-gray-700 hover:border-cyan-500/50 p-6 rounded-lg transition-all duration-300 hover:bg-gray-800/50">
                <div className="font-mono text-cyan-400 text-sm mb-4">
                  <span className="text-gray-500">{t('professionalProfile')}</span>
                </div>
                <a 
                  href="https://www.linkedin.com/in/ikerperaltacondes/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center space-x-4 group cursor-pointer"
                >
                  <div className="w-12 h-12 flex items-center justify-center bg-blue-600/20 border border-blue-500/30 rounded-lg group-hover:bg-blue-600/30 group-hover:border-blue-400 transition-all duration-300">
                    <i className="ri-linkedin-fill text-blue-400 text-xl"></i>
                  </div>
                  <div className="flex-1">
                    <h4 className="text-white font-medium mb-1 font-mono text-sm group-hover:text-cyan-400 transition-colors duration-300">
                      {t('linkedinTitle')}
                    </h4>
                    <p className="text-gray-400 text-sm font-mono group-hover:text-gray-300 transition-colors duration-300">
                      {t('linkedinDesc')}
                    </p>
                  </div>
                  <div className="text-cyan-400 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <i className="ri-external-link-line text-lg"></i>
                  </div>
                </a>
              </div>
            </div>

            {/* Skills grid */}
            <div className="space-y-4">
              <div className="font-mono text-cyan-400 text-sm mb-6">
                <span className="text-gray-500">{t('coreCompetencies')}</span>
              </div>
              {skills.map((skill, index) => (
                <div 
                  key={index}
                  className={`bg-black/30 border border-gray-700 hover:border-cyan-500/50 p-4 rounded-lg transition-all duration-300 hover:bg-gray-800/50 ${
                    isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'
                  }`}
                  style={{ transitionDelay: `${index * 100}ms` }}
                >
                  <div className="flex items-start space-x-4">
                    <div className="w-8 h-8 flex items-center justify-center text-cyan-400 text-xl">
                      <i className={skill.icon}></i>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-white font-medium mb-1 font-mono text-sm">
                        {skill.title}
                      </h4>
                      <p className="text-gray-400 text-sm font-mono">
                        {skill.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
